import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import adminApi from '../../services/adminApi';
import toast from 'react-hot-toast';
import { EnvelopeIcon, CheckCircleIcon, XMarkIcon, ArchiveBoxIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../../hooks/useConfirmationModal';

interface ContactMessageDto {
  id: string;
  name: string;
  email: string;
  category?: string;
  subject: string;
  message: string;
  ipAddress?: string;
  status: number;
  createdAt: string;
  readAt?: string;
  readByAdminId?: string;
  adminNotes?: string;
}

const AdminContactMessagesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedMessage, setSelectedMessage] = useState<ContactMessageDto | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();

  // Fetch contact messages
  const { data: contactMessages, isLoading, error } = useQuery({
    queryKey: ['admin-contact-messages', page],
    queryFn: () => adminApi.getContactMessages(page, pageSize),
  });

  // Fetch unread count
  const { data: unreadCount } = useQuery({
    queryKey: ['admin-contact-messages-unread'],
    queryFn: () => adminApi.getContactMessagesUnreadCount(),
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, adminNotes }: { id: string; status: number; adminNotes?: string }) =>
      adminApi.updateContactMessageStatus(id, { status, adminNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages'] });
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages-unread'] });
      toast.success('Message status updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error updating message status');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteContactMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages'] });
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages-unread'] });
      toast.success('Message deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error deleting message');
    },
  });

  const getStatusBadge = (status: number) => {
    const statusMap: Record<number, { label: string; color: string; icon: React.ReactNode }> = {
      0: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: <EnvelopeIcon className="h-4 w-4" /> },
      1: { label: 'Read', color: 'bg-blue-100 text-blue-800', icon: <CheckCircleIcon className="h-4 w-4" /> },
      2: { label: 'Replied', color: 'bg-green-100 text-green-800', icon: <CheckCircleIcon className="h-4 w-4" /> },
      3: { label: 'Spam', color: 'bg-red-100 text-red-800', icon: <ExclamationTriangleIcon className="h-4 w-4" /> },
      4: { label: 'Archived', color: 'bg-gray-100 text-gray-800', icon: <ArchiveBoxIcon className="h-4 w-4" /> },
    };

    const statusInfo = statusMap[status] || statusMap[0];
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.icon}
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleViewMessage = async (message: ContactMessageDto) => {
    setSelectedMessage(message);
    setIsViewModalOpen(true);

    // Mark as read if not already read
    if (message.status === 0) {
      updateStatusMutation.mutate({ id: message.id, status: 1 });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Error loading contact messages</p>
      </div>
    );
  }

  return (
    <div className="p-6" dir="ltr">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Contact Messages</h1>
            <p className="text-gray-600 mt-2">Manage and respond to contact form submissions</p>
          </div>
          {(unreadCount?.data?.count ?? 0) > 0 && (
            <div className="bg-red-100 text-red-800 px-4 py-2 rounded-lg font-semibold">
              {unreadCount?.data?.count} Unread
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <EnvelopeIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 0).length || 0}
              </h3>
              <p className="text-gray-600">Pending</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 1).length || 0}
              </h3>
              <p className="text-gray-600">Read</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 2).length || 0}
              </h3>
              <p className="text-gray-600">Replied</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 3).length || 0}
              </h3>
              <p className="text-gray-600">Spam</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Contact Messages</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contactMessages?.data?.map((message: ContactMessageDto) => (
                <tr key={message.id} className={`hover:bg-gray-50 ${message.status === 0 ? 'bg-yellow-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{message.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{message.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{message.category || 'General'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">{message.subject}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(message.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(message.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleViewMessage(message)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                      >
                        View
                      </button>
                      {message.status === 0 && (
                        <button
                          onClick={() => updateStatusMutation.mutate({ id: message.id, status: 1 })}
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                        >
                          Mark Read
                        </button>
                      )}
                      {message.status !== 3 && (
                        <button
                          onClick={() => updateStatusMutation.mutate({ id: message.id, status: 3 })}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        >
                          Mark Spam
                        </button>
                      )}
                      <button
                        onClick={() => {
                          showModal(modalPresets.deleteEn('this message', () => {
                            deleteMutation.mutate(message.id);
                          }));
                        }}
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">Page {page}</span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={!contactMessages?.data || contactMessages.data.length < pageSize}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* View Message Modal */}
      {isViewModalOpen && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900">Contact Message</h2>
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedMessage(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                  <p className="text-gray-900">{selectedMessage.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                  <p className="text-gray-900">{selectedMessage.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                  <p className="text-gray-900">{selectedMessage.category || 'General Inquiry'}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                  {getStatusBadge(selectedMessage.status)}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Date</label>
                  <p className="text-gray-900">{formatDate(selectedMessage.createdAt)}</p>
                </div>
                {selectedMessage.ipAddress && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">IP Address</label>
                    <p className="text-gray-900 text-sm">{selectedMessage.ipAddress}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
                <p className="text-gray-900">{selectedMessage.subject}</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
                <div className="bg-gray-50 rounded-lg p-4 text-gray-900 whitespace-pre-wrap">
                  {selectedMessage.message}
                </div>
              </div>
              {selectedMessage.adminNotes && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Admin Notes</label>
                  <p className="text-gray-600">{selectedMessage.adminNotes}</p>
                </div>
              )}
              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedMessage(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Close
                </button>
                {selectedMessage.status === 0 && (
                  <button
                    onClick={() => {
                      updateStatusMutation.mutate({ id: selectedMessage.id, status: 1 });
                    }}
                    className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    Mark as Read
                  </button>
                )}
                {selectedMessage.status !== 2 && (
                  <button
                    onClick={() => {
                      updateStatusMutation.mutate({ id: selectedMessage.id, status: 2 });
                    }}
                    className="px-6 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors duration-200"
                  >
                    Mark as Replied
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'Confirm'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'warning'}
      />
    </div>
  );
};

export default AdminContactMessagesPage;

